import { Group, SwapHoriz } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { getComitees } from 'lib/api';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Committee, CommitteeChangeDialogProps } from './types';

const CommitteeChangeDialog = ({
  open,
  setOpen,
  currentComitee,
  onSave,
  onCancel,
}: CommitteeChangeDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [comitees, setComitees] = useState<Committee[]>([]);
  const [newComitee, setNewComitee] = useState<string>('');
  const dispatch = useDispatch();

  const validateAttributes = () => {
    if (!newComitee) {
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateAttributes()) {
      return;
    }
    setLoading(true);
    try {
      await onSave(newComitee);
      setOpen(false);
    } catch (error) {
      console.error('Error al cambiar el comité:', error);
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setNewComitee('');
    onCancel();
    setOpen(false);
  };

  const setUpComitees = async () => {
    setLoading(true);
    try {
      const response = await getComitees(dispatch);
      setComitees(response);
    } catch (error) {
      console.error('Error al obtener comités:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) void setUpComitees();
  }, [open]);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SwapHoriz sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Cambiar Comité
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          Selecciona el nuevo comité que revisará esta solicitud.
        </Typography>

        <Paper
          sx={{
            p: 2,
            mb: 3,
            boxShadow: 'none',
            border: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
          }}
        >
          <Typography variant="body2" fontWeight={'bold'} sx={{ mb: 1 }}>
            Comité actual
          </Typography>
          <Typography variant="body1">
            {currentComitee ? currentComitee.name : 'Sin comité seleccionado'}
          </Typography>
        </Paper>

        <FormControl fullWidth>
          <InputLabel id="comitee-select-label">Nuevo comité</InputLabel>
          <Select
            labelId="comitee-select-label"
            value={newComitee}
            onChange={(e) => setNewComitee(e.target.value)}
            label="Nuevo comité"
            error={!newComitee && comitees.length > 0}
          >
            {comitees
              .filter((comitee: Committee) => comitee.id !== currentComitee?.id)
              .map((comitee: Committee, index: number) => (
                <MenuItem key={index} value={comitee.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Group sx={{ fontSize: '1rem', color: 'primary.main' }} />
                    <Typography>{comitee.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton
          onClick={handleCancel}
          color="secondary"
          disabled={loading}
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleSave}
          color="primary"
          disabled={loading || !validateAttributes()}
        >
          {loading ? 'Cambiando...' : 'Guardar'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default CommitteeChangeDialog;
